import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';
import { commonAPI } from '../services/api'; // adjust path

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [regCode, setRegCode] = useState('');
  const [googleToken, setGoogleToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null); // { name, motto, logo_base64, ... }
  const [loadingSchool, setLoadingSchool] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  // Read reg_code from URL and fetch school config
  useEffect(() => {
    const urlRegCode = searchParams.get('reg_code');
    if (urlRegCode) {
      setRegCode(urlRegCode); // pre-fill the input
      setLoadingSchool(true);
      commonAPI.getSchoolConfig(urlRegCode)
        .then(response => {
          setSchoolInfo(response.data); // store school details
        })
        .catch(error => {
          console.error('School not found or error:', error);
          setSchoolInfo(null); // fallback to default branding
        })
        .finally(() => setLoadingSchool(false));
    } else {
      // No reg_code in URL – reset to default
      setSchoolInfo(null);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (googleToken) {
      const result = await googleLogin(googleToken, regCode);
      if (!result.success) setLoading(false);
    } else {
      const result = await login(email, password, regCode);
      if (!result.success) setLoading(false);
    }
  };

  // Determine display title and branding
  const displayTitle = schoolInfo?.name || 'SchoolSuite System';
  const displayMotto = schoolInfo?.motto;
  const displayLogo = schoolInfo?.logo_base64;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
      <div className="max-w-md w-full mx-4 space-y-8 p-6 md:p-8 bg-white rounded-2xl shadow-xl">
        {/* Branding / Welcome Area */}
        <div className="text-center">
          {displayLogo ? (
            <div className="flex justify-center mb-4">
              <img 
                src={displayLogo} 
                alt={displayTitle} 
                className="h-16 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-primary-100 rounded-full flex items-center justify-center">
                <i className="fas fa-school text-2xl md:text-3xl text-primary-600"></i>
              </div>
            </div>
          )}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{displayTitle}</h2>
          {displayMotto && (
            <p className="mt-1 text-sm italic text-gray-500">“{displayMotto}”</p>
          )}
          <p className="mt-2 text-sm md:text-base text-gray-600">Sign in to your account</p>
          {loadingSchool && <p className="text-xs text-gray-400 mt-2">Loading school info...</p>}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
                  <i className="fas fa-envelope"></i>
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required={!googleToken}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!googleToken}
                  className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
                  <i className="fas fa-lock"></i>
                </span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required={!googleToken}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!!googleToken}
                  className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Registration Code Field - always visible, pre-filled if from URL */}
            <div>
              <label htmlFor="regCode" className="block text-sm font-medium text-gray-700 mb-1">
                School Registration Code
              </label>
              <div className="relative">
                <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
                  <i className="fas fa-qrcode"></i>
                </span>
                <input
                  id="regCode"
                  name="regCode"
                  type="text"
                  required
                  value={regCode}
                  onChange={(e) => setRegCode(e.target.value)}
                  className="w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
                  placeholder="Enter your school code"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {schoolInfo 
                  ? "✓ Valid school detected. You may change the code to log into another school." 
                  : "Enter your school's registration code"}
              </p>
            </div>
          </div>

          {/* Google Login Button */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                setGoogleToken(credentialResponse.credential);
                toast.success('Google authentication successful. Please enter your school code and sign in.');
              }}
              onError={() => toast.error('Google sign-in failed')}
              text="signin_with"
              shape="pill"
              theme="outline"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>
            <div className="text-sm">
              <a href="/forgot_password" className="font-medium text-primary-600 hover:text-primary-500 text-sm">
                Forgot your password?
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Signing in...
                </>
              ) : (
                <>
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <i className="fas fa-sign-in-alt text-sm md:text-base"></i>
                  </span>
                  Sign in
                </>
              )}
            </button>
          </div>

          <div className="text-center text-gray-600">
            <div className="mb-4">
              <p className="text-xs text-gray-500">
                <a
                  href="/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Create Account
                </a>
              </p>
              <p className="text-xs text-gray-500">
                <a
                  href="/school/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Create School
                </a>
              </p>
            </div>

            {/* Info Panel – unchanged, still contains hosted by cbthost.com */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <div className="bg-blue-50 rounded-lg p-3 text-left text-xs text-blue-800 space-y-2">
                <p className="font-semibold">📘 About SchoolSuite</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    <a
                      href="https://cbthost.com/school_management_software"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      Guidelines & features
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://cbthost.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      Download offline desktop version
                    </a>
                    {' '}(create quizzes offline)
                  </li>
                  <li>
                    <strong>Staff accounts:</strong> Use Gmail addresses only.
                  </li>
                  <li>
                    Students log in easily with their tag ID.
                  </li>
                </ul>
              </div>
              <p className="text-xs text-gray-500">
                Need help?{' '}
                <a
                  href="https://cbthost.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  Contact support
                </a>
              </p>
              <p className="text-xs text-gray-500">
                Powered and hosted by{' '}
                <a
                  href="https://cbthost.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  cbthost.com
                </a>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;

// import React, { useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../context/AuthContext';
// import { GoogleLogin } from '@react-oauth/google';
// import toast from 'react-hot-toast';

// const Login = () => {
//   const [email, setEmail] = useState('');
//   const [password, setPassword] = useState('');
//   const [regCode, setRegCode] = useState('');
//   const [googleToken, setGoogleToken] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const { login, googleLogin } = useAuth();
//   const navigate = useNavigate();

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     if (googleToken) {
//       // Google login flow
//       const result = await googleLogin(googleToken, regCode);
//       if (!result.success) setLoading(false);
//     } else {
//       // Manual login flow
//       const result = await login(email, password, regCode);
//       if (!result.success) setLoading(false);
//     }
//   };

//   return (
//     <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
//       <div className="max-w-md w-full mx-4 space-y-8 p-6 md:p-8 bg-white rounded-2xl shadow-xl">
//         <div className="text-center">
//           <div className="flex justify-center mb-6">
//             <div className="w-14 h-14 md:w-16 md:h-16 bg-primary-100 rounded-full flex items-center justify-center">
//               <i className="fas fa-school text-2xl md:text-3xl text-primary-600"></i>
//             </div>
//           </div>
//           <h2 className="text-2xl md:text-3xl font-bold text-gray-900">SchoolSuite System</h2>
//           <p className="mt-2 text-sm md:text-base text-gray-600">Sign in to your account</p>
//         </div>

//         <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
//           <div className="space-y-4">
//             {/* Email Field */}
//             <div>
//               <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
//                 Email Address
//               </label>
//               <div className="relative">
//                 <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
//                   <i className="fas fa-envelope"></i>
//                 </span>
//                 <input
//                   id="email"
//                   name="email"
//                   type="email"
//                   autoComplete="email"
//                   required={!googleToken}
//                   value={email}
//                   onChange={(e) => setEmail(e.target.value)}
//                   disabled={!!googleToken}
//                   className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
//                   placeholder="Enter your email"
//                 />
//               </div>
//             </div>

//             {/* Password Field */}
//             <div>
//               <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
//                 Password
//               </label>
//               <div className="relative">
//                 <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
//                   <i className="fas fa-lock"></i>
//                 </span>
//                 <input
//                   id="password"
//                   name="password"
//                   type="password"
//                   autoComplete="current-password"
//                   required={!googleToken}
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   disabled={!!googleToken}
//                   className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
//                   placeholder="Enter your password"
//                 />
//               </div>
//             </div>

//             {/* Registration Code Field */}
//             <div>
//               <label htmlFor="regCode" className="block text-sm font-medium text-gray-700 mb-1">
//                 School Registration Code
//               </label>
//               <div className="relative">
//                 <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
//                   <i className="fas fa-qrcode"></i>
//                 </span>
//                 <input
//                   id="regCode"
//                   name="regCode"
//                   type="text"
//                   required
//                   value={regCode}
//                   onChange={(e) => setRegCode(e.target.value)}
//                   className="w-full pl-12 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
//                   placeholder="Enter your school code"
//                 />
//               </div>
//             </div>
//           </div>

//           {/* Google Login Button */}
//           <div className="flex justify-center">
//             <GoogleLogin
//               onSuccess={(credentialResponse) => {
//                 setGoogleToken(credentialResponse.credential);
//                 toast.success('Google authentication successful. Please enter your school code and sign in.');
//               }}
//               onError={() => toast.error('Google sign-in failed')}
//               text="signin_with"
//               shape="pill"
//               theme="outline"
//             />
//           </div>

//           <div className="flex items-center justify-between">
//             <div className="flex items-center">
//               <input
//                 id="remember-me"
//                 name="remember-me"
//                 type="checkbox"
//                 className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
//               />
//               <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
//                 Remember me
//               </label>
//             </div>

//             <div className="text-sm">
//               <a href="/forgot_password" className="font-medium text-primary-600 hover:text-primary-500 text-sm">
//                 Forgot your password?
//               </a>
//             </div>
//           </div>

//           <div>
//             <button
//               type="submit"
//               disabled={loading}
//               className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               {loading ? (
//                 <>
//                   <i className="fas fa-spinner fa-spin mr-2"></i>
//                   Signing in...
//                 </>
//               ) : (
//                 <>
//                   <span className="absolute left-0 inset-y-0 flex items-center pl-3">
//                     <i className="fas fa-sign-in-alt text-sm md:text-base"></i>
//                   </span>
//                   Sign in
//                 </>
//               )}
//             </button>
//           </div>

//           <div className="text-center text-gray-600">
//             <div className="mb-4">
//               <p className="text-xs text-gray-500">
//                 <a
//                   href="/register"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
//                 >
//                   Create Account
//                 </a>
//               </p>
//               <p className="text-xs text-gray-500">
//                 <a
//                   href="/school/register"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
//                 >
//                   Create School
//                 </a>
//               </p>
//             </div>

//             {/* Info Panel – SchoolSuite features, guidelines, support */}
//             <div className="pt-4 border-t border-gray-200 space-y-3">
//               <div className="bg-blue-50 rounded-lg p-3 text-left text-xs text-blue-800 space-y-2">
//                 <p className="font-semibold">📘 About SchoolSuite</p>
//                 <ul className="list-disc list-inside space-y-1">
//                   <li>
//                     <a
//                       href="https://cbthost.com/school_management_software"
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-primary-600 hover:underline"
//                     >
//                       Guidelines & features
//                     </a>
//                   </li>
//                   <li>
//                     <a
//                       href="https://cbthost.com/download"
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-primary-600 hover:underline"
//                     >
//                       Download offline desktop version
//                     </a>
//                     {' '}(create quizzes offline)
//                   </li>
//                   <li>
//                     <strong>Staff accounts:</strong> Use Gmail addresses only.
//                   </li>
//                   <li>
//                     Students log in easily with their tag ID.
//                   </li>
//                 </ul>
//               </div>

//               <p className="text-xs text-gray-500">
//                 Need help?{' '}
//                 <a
//                   href="https://cbthost.com/contact"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
//                 >
//                   Contact support
//                 </a>
//               </p>
//               <p className="text-xs text-gray-500">
//                 Powered and hosted by{' '}
//                 <a
//                   href="https://cbthost.com"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
//                 >
//                   cbthost.com
//                 </a>
//               </p>
//             </div>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default Login;